// 토스트 알림 시스템 - alert() 대체
import { createApp, ref } from 'vue'
import Toast from '@/components/ui/Toast.vue'

const toasts = ref([])

let toastId = 0

/**
 * 사용자 친화적인 에러 메시지 매핑
 */
const errorMessages = {
  // 권한 관련
  'access_denied': {
    title: '권한 없음',
    message: '이 기능을 사용할 권한이 없습니다.\n관리자에게 문의해주세요.',
    type: 'error'
  },
  '권한이 없습니다.': {
    title: '접근 제한',
    message: '이 페이지에 접근할 권한이 없습니다.\n관리자에게 문의해주세요.',
    type: 'error'
  },
  
  // 인증 관련
  'unauthorized': {
    title: '로그인 필요',
    message: '로그인이 필요한 서비스입니다.\n다시 로그인해주세요.',
    type: 'warning'
  },
  '토큰이 만료되었습니다.': {
    title: '세션 만료',
    message: '로그인 세션이 만료되었습니다.\n다시 로그인해주세요.',
    type: 'warning'
  },
  
  // 서버 에러
  'internal_server_error': {
    title: '서버 오류',
    message: '일시적인 서버 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.',
    type: 'error'
  },
  'bad_gateway': {
    title: '연결 오류',
    message: '서버 연결에 문제가 발생했습니다.\n잠시 후 다시 시도해주세요.',
    type: 'error'
  },
  
  // 네트워크 에러
  'network_error': {
    title: '네트워크 오류',
    message: '인터넷 연결을 확인하고\n다시 시도해주세요.',
    type: 'error'
  },
  
  // 기본 메시지
  'default': {
    title: '알림',
    message: '요청을 처리하는 중 문제가 발생했습니다.',
    type: 'error'
  }
}

/**
 * 에러 메시지를 사용자 친화적으로 변환
 */
function getErrorMessage(error) {
  // 문자열로 변환
  const errorStr = typeof error === 'string' ? error : error?.message || 'unknown'
  const lowerError = errorStr.toLowerCase()
  
  // 직접 매칭 시도
  if (errorMessages[errorStr]) {
    return errorMessages[errorStr]
  }
  
  // 부분 매칭 시도
  if (lowerError.includes('access') && lowerError.includes('denied')) {
    return errorMessages['access_denied']
  }
  
  if (lowerError.includes('unauthorized') || lowerError.includes('401')) {
    return errorMessages['unauthorized']
  }
  
  if (lowerError.includes('forbidden') || lowerError.includes('403')) {
    return errorMessages['권한이 없습니다.']
  }
  
  if (lowerError.includes('server') || lowerError.includes('500')) {
    return errorMessages['internal_server_error']
  }
  
  if (lowerError.includes('network') || lowerError.includes('fetch')) {
    return errorMessages['network_error']
  }
  
  if (lowerError.includes('gateway') || lowerError.includes('502') || lowerError.includes('503')) {
    return errorMessages['bad_gateway']
  }
  
  // 기본 메시지
  return {
    title: '알림',
    message: errorStr,
    type: 'error'
  }
}

/**
 * 토스트 표시 함수
 */
function showToast(options) {
  const id = ++toastId
  
  const defaultOptions = {
    type: 'info',
    duration: 4000,
    position: 'toast--top-right',
    closable: true
  }
  
  const toastOptions = { ...defaultOptions, ...options, id }
  
  // DOM에 토스트 컨테이너 생성
  const container = document.createElement('div')
  document.body.appendChild(container)
  
  const app = createApp(Toast, {
    ...toastOptions,
    onClose: () => {
      app.unmount()
      document.body.removeChild(container)
      toasts.value = toasts.value.filter(t => t.id !== id)
    }
  })
  
  app.mount(container)
  toasts.value.push({ id, ...toastOptions })
  
  return id
}

/**
 * 토스트 조합 함수
 */
export function useToast() {
  return {
    // 기본 토스트
    show: showToast,
    
    // 타입별 헬퍼 함수
    success: (message, options = {}) => showToast({
      type: 'success',
      title: '성공',
      message,
      ...options
    }),
    
    error: (error, options = {}) => {
      const errorInfo = getErrorMessage(error)
      return showToast({
        type: 'error',
        title: errorInfo.title,
        message: errorInfo.message,
        duration: 6000, // 에러는 조금 더 오래 표시
        ...options
      })
    },
    
    warning: (message, options = {}) => showToast({
      type: 'warning',
      title: '주의',
      message,
      ...options
    }),
    
    info: (message, options = {}) => showToast({
      type: 'info',
      title: '안내',
      message,
      ...options
    }),
    
    // 권한 관련 전용 함수
    accessDenied: (customMessage = null) => showToast({
      type: 'error',
      title: '접근 권한 없음',
      message: customMessage || '이 기능을 사용할 권한이 없습니다.\n관리자에게 문의해주세요.',
      duration: 6000
    }),
    
    // 로그인 필요 알림
    loginRequired: () => showToast({
      type: 'warning',
      title: '로그인 필요',
      message: '로그인이 필요한 서비스입니다.',
      duration: 4000
    }),
    
    // 모든 토스트 제거
    clear: () => {
      toasts.value.forEach(toast => {
        const elements = document.querySelectorAll('.toast')
        elements.forEach(el => el.remove())
      })
      toasts.value = []
    },
    
    // 현재 표시된 토스트 목록
    toasts: toasts
  }
}

/**
 * alert() 대체 함수 - 기존 코드 마이그레이션용
 */
export function smartAlert(message, type = 'info') {
  const toast = useToast()
  
  // 에러 타입 자동 감지
  if (typeof message === 'string') {
    const lowerMessage = message.toLowerCase()
    
    if (lowerMessage.includes('성공') || lowerMessage.includes('완료') || lowerMessage.includes('등록')) {
      return toast.success(message)
    }
    
    if (lowerMessage.includes('권한') || lowerMessage.includes('access') || lowerMessage.includes('forbidden')) {
      return toast.accessDenied(message)
    }
    
    if (lowerMessage.includes('오류') || lowerMessage.includes('실패') || lowerMessage.includes('error')) {
      return toast.error(message)
    }
    
    if (lowerMessage.includes('주의') || lowerMessage.includes('warning')) {
      return toast.warning(message)
    }
  }
  
  // 기본 타입으로 표시
  return toast[type](message)
}

// 전역에서 사용할 수 있도록 window 객체에 등록 (선택사항)
if (typeof window !== 'undefined') {
  window.smartAlert = smartAlert
}